import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Action, Select, Selector, State, StateContext, Store } from "@ngxs/store";
import { Observable, tap } from "rxjs";

import { Order, OrderCheckout } from "../../interface/order.interface";

import { NotificationService } from "../../services/notification.service";
import { OrderService } from "../../services/order.service";

import { Cart } from "../../interface/cart.interface";
import { General, Setting, Values } from "../../interface/setting.interface";
import { CommonService } from "../../services/common.service";
import { Checkout, DownloadInvoice, GetOrders, OrderTracking, PlaceOrder, RePayment, ViewOrder } from "../action/order.action";
import { CartState } from "./cart.state";
import { SettingState } from "./setting.state";

export class OrderStateModel {
  order = {
    data: [] as Order[],
    total: 0
  }
  selectedOrder: Order | null
  checkout: OrderCheckout | null
}

@State<OrderStateModel>({
  name: "order",
  defaults: {
    order: {
      data: [],
      total: 0
    },
    selectedOrder: null,
    checkout: null
  },
})
@Injectable()
export class OrderState {
  @Select(CartState.cartItems) cartItem$: Observable<Cart[]>;
  @Select(SettingState.setting) setting$: Observable<Values>;
  private settings: Values = {} as Values;
  private cartItems: Cart[] = [];

  constructor(private notificationService: NotificationService,
    private router: Router,
    private store: Store,
    private commonService: CommonService,
    private orderService: OrderService) {
    this.cartItem$.subscribe(cartItems => this.cartItems = cartItems);
    this.setting$.subscribe(setting => this.settings = setting);

  }

  @Selector()
  static order(state: OrderStateModel) {
    return state.order;
  }

  @Selector()
  static selectedOrder(state: OrderStateModel) {
    return state.selectedOrder;
  }

  @Selector()
  static checkout(state: OrderStateModel) {
    return state.checkout;
  }

  @Action(GetOrders)
  getOrders(ctx: StateContext<OrderStateModel>, action: GetOrders) {
    return this.orderService.getOrders(action?.payload).pipe(
      tap({
        next: result => {
          ctx.patchState({
            order: {
              data: result.data,
              total: result?.total ? result?.total : result.data?.length
            }
          });
        },
        error: err => {
          throw new Error(err?.error?.message);
        }
      })
    );
  }

  @Action(ViewOrder)
  viewOrder(ctx: StateContext<OrderStateModel>, { id }: ViewOrder) {
    this.orderService.skeletonLoader = true;
    return this.orderService.getOrders().pipe(
      tap({
        next: results => {
          if (results && results.data) {
            const state = ctx.getState();
            const result = results.data.find(order => order.order_number == id);
            ctx.patchState({
              ...state,
              selectedOrder: result
            });
          }
        },
        error: err => {
          throw new Error(err?.error?.message);
        },
        complete: () => {
          this.orderService.skeletonLoader = false;
        }
      })
    );
  }

  @Action(Checkout)
  checkout(ctx: StateContext<OrderStateModel>, action: Checkout) {
    const state = ctx.getState();
    const subTotal = this.cartItems.reduce((accumulator, item) => accumulator + item.sub_total, 0);
    const shippingFee = this.settings.general.min_order_free_shipping;
    const totalBeforeTax = subTotal + shippingFee;
    const taxTotal = this.commonService.calculateTaxAmount(totalBeforeTax, this.settings.general.tax_percentage);
    const order = {
      total: {
        convert_point_amount: 0,
        convert_wallet_balance: 0,
        coupon_total_discount: 0,
        points: 0,
        points_amount: 0,
        sub_total: subTotal,
        shipping_total: shippingFee,
        tax_total: taxTotal,
        total: totalBeforeTax + taxTotal,
        wallet_balance: 0,
      }
    };

    ctx.patchState({
      ...state,
      checkout: order
    });
  }

  @Action(PlaceOrder)
  placeOrder(ctx: StateContext<OrderStateModel>, action: PlaceOrder) {
    // Place order Logic Here
  }

  @Action(RePayment)
  rePayment(ctx: StateContext<OrderStateModel>, action: RePayment) {
    // Repayment Logic Here
  }

  @Action(OrderTracking)
  orderTracking(ctx: StateContext<OrderStateModel>, action: OrderTracking) {
    // this.notificationService.notification = false;
    return this.orderService.orderTracking(action.payload).pipe(
      tap({
        next: result => {
          const state = ctx.getState();
          ctx.patchState({
            ...state,
            selectedOrder: result
          });
        },
        error: err => {
          throw new Error(err?.error?.message);
        }
      })
    );
  }

  @Action(DownloadInvoice)
  downloadInvoice(ctx: StateContext<OrderStateModel>, action: DownloadInvoice) {
    // Download invoice Logic Here
  }

}
